import { ChangeEvent } from "react";

import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { InputLabel, Typography } from "components";
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
  useFormContext,
} from "react-hook-form";
import { getErrorMessage } from "utils";

const RadioHookForm = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  label,
  options,
  disabled,
  tooltipText,
  required,
  row = true,
}: Omit<ControllerProps<TFieldValues, TName>, "control" | "render"> & {
  label: string;
  options: {
    label: string;
    value: string;
    subLabel?: string;
    afterNode?: React.ReactNode;
  }[];
  tooltipText?: string;
  required?: boolean;
  row?: boolean;
}) => {
  const { control } = useFormContext<TFieldValues>();
  return (
    <>
      <InputLabel required={required} helperText={tooltipText}>
        {label}
      </InputLabel>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, formState: { errors } }) => {
          const error = getErrorMessage(errors, name);
          const errorMsg = error?.message;
          return (
            <>
              <RadioGroup
                row={row}
                value={String(value)}
                sx={{
                  "& label:not(:last-child)": {
                    marginBottom: "0px",
                  },
                }}
                onChange={(_e, v) =>
                  onChange(
                    v as unknown as
                      | PathValue<TFieldValues, TName>
                      | ChangeEvent<Element>
                  )
                }
              >
                {options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={String(option.value)}
                    label={
                      <Stack direction="row" alignItems="center">
                        <Box marginLeft="5.5px">
                          <InputLabel
                            mb="0px"
                            htmlFor={`${name}${option.value}`}
                            sx={{ cursor: "pointer" }}
                          >
                            {option.label}
                          </InputLabel>
                          <Typography type="TextXS" color="#475467">
                            {option.subLabel || ""}
                          </Typography>
                        </Box>
                        {option?.afterNode}
                      </Stack>
                    }
                    control={
                      <Radio
                        size="small"
                        id={`${name}${option.value}`}
                        disabled={disabled}
                      />
                    }
                  />
                ))}
              </RadioGroup>
              {!!errorMsg && (
                <FormHelperText variant="outlined" error={!!errorMsg}>
                  {String(errorMsg || "")}
                </FormHelperText>
              )}
            </>
          );
        }}
      />
    </>
  );
};

export default RadioHookForm;
